import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('insurelab_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Auth
  register: async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  },

  login: async (email, password, totpCode = null) => {
    const response = await axios.post(`${API}/auth/login`, { email, password, totp_code: totpCode });
    return response.data;
  },

  getMe: async () => {
    const response = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
    return response.data;
  },

  // 2FA
  enable2FA: async () => {
    const response = await axios.post(`${API}/auth/2fa/enable`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  verify2FA: async (totpCode) => {
    const response = await axios.post(`${API}/auth/2fa/verify`, { totp_code: totpCode }, { headers: getAuthHeaders() });
    return response.data;
  },

  disable2FA: async (totpCode) => {
    const response = await axios.post(`${API}/auth/2fa/disable`, { totp_code: totpCode }, { headers: getAuthHeaders() });
    return response.data;
  },

  // OAuth
  googleOAuth: async (credential) => {
    const response = await axios.post(`${API}/auth/oauth/google`, { credential });
    return response.data;
  },

  // Templates
  getTemplates: async () => {
    const response = await axios.get(`${API}/templates`);
    return response.data;
  },

  applyTemplate: async (templateId) => {
    const response = await axios.post(`${API}/templates/${templateId}/apply`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  // Coverage Blocks
  getCoverageBlocks: async (category = null) => {
    const params = category ? { category } : {};
    const response = await axios.get(`${API}/coverage-blocks`, { params });
    return response.data;
  },

  // Policies
  createPolicy: async () => {
    const response = await axios.post(`${API}/policies`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  getPolicies: async () => {
    const response = await axios.get(`${API}/policies`, { headers: getAuthHeaders() });
    return response.data;
  },

  getPolicy: async (policyId) => {
    const response = await axios.get(`${API}/policies/${policyId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  updatePolicy: async (policyId, updates) => {
    const response = await axios.put(`${API}/policies/${policyId}`, updates, { headers: getAuthHeaders() });
    return response.data;
  },

  addBlockToPolicy: async (policyId, blockData) => {
    const response = await axios.post(`${API}/policies/${policyId}/add-block`, blockData, { headers: getAuthHeaders() });
    return response.data;
  },

  removeBlockFromPolicy: async (policyId, blockId) => {
    const response = await axios.delete(`${API}/policies/${policyId}/blocks/${blockId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  acceptTerms: async (policyId) => {
    const response = await axios.post(`${API}/policies/${policyId}/accept-terms`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  downloadCOI: async (policyId) => {
    const response = await axios.get(`${API}/policies/${policyId}/coi`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `InsureLab_COI_${policyId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Chat
  sendChatMessage: async (message, sessionId = null, policyId = null) => {
    const response = await axios.post(`${API}/chat`, { message, session_id: sessionId, policy_id: policyId }, { headers: getAuthHeaders() });
    return response.data;
  },

  // Admin
  getAnalytics: async () => {
    const response = await axios.get(`${API}/admin/analytics`, { headers: getAuthHeaders() });
    return response.data;
  },

  getUsers: async (role = null, orgId = null) => {
    const params = {};
    if (role) params.role = role;
    if (orgId) params.org_id = orgId;
    const response = await axios.get(`${API}/admin/users`, { params, headers: getAuthHeaders() });
    return response.data;
  },

  updateUserRole: async (userId, role, orgId = null) => {
    const response = await axios.put(`${API}/admin/users/${userId}/role`, { role, org_id: orgId }, { headers: getAuthHeaders() });
    return response.data;
  },

  getAuditLogs: async (limit = 50) => {
    const response = await axios.get(`${API}/admin/audit-logs`, { params: { limit }, headers: getAuthHeaders() });
    return response.data;
  },

  // Organizations
  getOrganizations: async () => {
    const response = await axios.get(`${API}/organizations`, { headers: getAuthHeaders() });
    return response.data;
  },

  createOrganization: async (orgData) => {
    const response = await axios.post(`${API}/organizations`, orgData, { headers: getAuthHeaders() });
    return response.data;
  },

  updateOrgBranding: async (orgId, branding) => {
    const response = await axios.put(`${API}/organizations/${orgId}/branding`, branding, { headers: getAuthHeaders() });
    return response.data;
  },

  // Legal
  getTerms: async () => {
    const response = await axios.get(`${API}/legal/terms`);
    return response.data;
  },

  getPrivacy: async () => {
    const response = await axios.get(`${API}/legal/privacy`);
    return response.data;
  },

  // Seed
  seedDatabase: async () => {
    const response = await axios.post(`${API}/seed`);
    return response.data;
  },

  // Checkout
  createCheckoutSession: async (policyId, billingCycle) => {
    const originUrl = window.location.origin;
    const response = await axios.post(`${API}/checkout/create-session`, {
      policy_id: policyId,
      billing_cycle: billingCycle,
      origin_url: originUrl
    }, { headers: getAuthHeaders() });
    return response.data;
  },

  getCheckoutStatus: async (sessionId) => {
    const response = await axios.get(`${API}/checkout/status/${sessionId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // PDF
  downloadPolicyPdf: async (policyId) => {
    const response = await axios.get(`${API}/policies/${policyId}/pdf`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    return response.data;
  },

  // Block positions
  updateBlockPositions: async (policyId, positions) => {
    const response = await axios.put(`${API}/policies/${policyId}/block-positions`, { positions }, { headers: getAuthHeaders() });
    return response.data;
  },

  // Public API docs
  getApiDocs: async () => {
    const response = await axios.get(`${API}/public/docs`);
    return response.data;
  },

  // Webhooks
  getWebhooks: async () => {
    const response = await axios.get(`${API}/webhooks`, { headers: getAuthHeaders() });
    return response.data;
  },

  registerWebhook: async (url, events) => {
    const response = await axios.post(`${API}/webhooks/register`, { url, events }, { headers: getAuthHeaders() });
    return response.data;
  },

  deleteWebhook: async (webhookId) => {
    const response = await axios.delete(`${API}/webhooks/${webhookId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  testWebhook: async (webhookId) => {
    const response = await axios.post(`${API}/webhooks/${webhookId}/test`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  // Public Quote (authenticated)
  generateQuote: async (coverages, industry, employeeCount) => {
    const response = await axios.post(`${API}/public/quote`, {
      coverages, industry, employee_count: employeeCount
    }, { headers: getAuthHeaders() });
    return response.data;
  },

  // Public Quote Calculator (no auth - lead capture)
  publicCalculateQuote: async (data) => {
    const response = await axios.post(`${API}/public/calculate`, data);
    return response.data;
  },

  savePublicLead: async (email, name, quoteId) => {
    const response = await axios.post(`${API}/public/save-lead`, { email, name, quote_id: quoteId });
    return response.data;
  },

  // Leads Dashboard (admin)
  getLeads: async () => {
    const response = await axios.get(`${API}/admin/leads`, { headers: getAuthHeaders() });
    return response.data;
  },

  getLeadsFunnel: async () => {
    const response = await axios.get(`${API}/admin/leads/funnel`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Claims
  createClaim: async (claimData) => {
    const response = await axios.post(`${API}/claims`, claimData, { headers: getAuthHeaders() });
    return response.data;
  },

  getClaims: async (statusFilter = null) => {
    const params = statusFilter ? { status_filter: statusFilter } : {};
    const response = await axios.get(`${API}/claims`, { params, headers: getAuthHeaders() });
    return response.data;
  },

  getClaim: async (claimId) => {
    const response = await axios.get(`${API}/claims/${claimId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  updateClaim: async (claimId, data) => {
    const response = await axios.put(`${API}/claims/${claimId}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  addClaimNote: async (claimId, text) => {
    const response = await axios.post(`${API}/claims/${claimId}/notes`, { text }, { headers: getAuthHeaders() });
    return response.data;
  },

  // Carrier
  submitToCarrier: async (policyId, carrierId) => {
    const response = await axios.post(`${API}/carrier/submit-policy`, { policy_id: policyId, carrier_id: carrierId }, { headers: getAuthHeaders() });
    return response.data;
  },

  getCarrierSubmissions: async () => {
    const response = await axios.get(`${API}/carrier/submissions`, { headers: getAuthHeaders() });
    return response.data;
  },

  respondToSubmission: async (submissionId, decision, notes, adjustedPremium, conditions) => {
    const response = await axios.put(`${API}/carrier/submissions/${submissionId}/respond`, {
      decision, notes, adjusted_premium: adjustedPremium, conditions
    }, { headers: getAuthHeaders() });
    return response.data;
  },

  // Analytics
  getRevenueAnalytics: async () => {
    const response = await axios.get(`${API}/admin/analytics/revenue`, { headers: getAuthHeaders() });
    return response.data;
  },

  getClaimsAnalytics: async () => {
    const response = await axios.get(`${API}/admin/analytics/claims`, { headers: getAuthHeaders() });
    return response.data;
  },

  getConversionAnalytics: async () => {
    const response = await axios.get(`${API}/admin/analytics/conversion`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Social OAuth
  socialOAuth: async (provider, data) => {
    const response = await axios.post(`${API}/auth/oauth/${provider}`, data);
    return response.data;
  },

  // E-Sign
  sendForEsign: async (policyId, signerEmail) => {
    const response = await axios.post(`${API}/esign/send`, { policy_id: policyId, signer_email: signerEmail }, { headers: getAuthHeaders() });
    return response.data;
  },

  getEsignStatus: async (envelopeId) => {
    const response = await axios.get(`${API}/esign/status/${envelopeId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // KYC
  submitKyc: async (data) => {
    const response = await axios.post(`${API}/kyc/submit`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  getMyKyc: async () => {
    const response = await axios.get(`${API}/kyc/my-verification`, { headers: getAuthHeaders() });
    return response.data;
  },

  getKycStatus: async (verificationId) => {
    const response = await axios.get(`${API}/kyc/status/${verificationId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  getKycQueue: async (statusFilter) => {
    const url = statusFilter ? `${API}/kyc/admin/queue?status_filter=${statusFilter}` : `${API}/kyc/admin/queue`;
    const response = await axios.get(url, { headers: getAuthHeaders() });
    return response.data;
  },

  getKycStats: async () => {
    const response = await axios.get(`${API}/kyc/admin/stats`, { headers: getAuthHeaders() });
    return response.data;
  },

  reviewKyc: async (verificationId, action, notes, reason) => {
    const response = await axios.put(`${API}/kyc/admin/${verificationId}/review`, { action, notes, reason }, { headers: getAuthHeaders() });
    return response.data;
  },

  // Branding
  getDefaultBranding: async () => {
    const response = await axios.get(`${API}/branding/default`);
    return response.data;
  },

  getOrgBranding: async (orgId) => {
    const response = await axios.get(`${API}/branding/${orgId}`);
    return response.data;
  },

  getMyBranding: async () => {
    const response = await axios.get(`${API}/branding/my/current`, { headers: getAuthHeaders() });
    return response.data;
  },

  updateBranding: async (data) => {
    const response = await axios.put(`${API}/branding/my/update`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  getAllBrandings: async () => {
    const response = await axios.get(`${API}/branding/admin/all`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Carriers
  getCarriers: async (type) => {
    const url = type ? `${API}/carriers?carrier_type=${type}` : `${API}/carriers`;
    const response = await axios.get(url);
    return response.data;
  },

  getCarrier: async (carrierId) => {
    const response = await axios.get(`${API}/carriers/${carrierId}`);
    return response.data;
  },

  getCarriersByProduct: async (productType) => {
    const response = await axios.get(`${API}/carriers/by-product/${productType}`);
    return response.data;
  },

  requestCarrierQuote: async (carrierId, data) => {
    const response = await axios.post(`${API}/carriers/${carrierId}/quote`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  requestMultiQuote: async (data) => {
    const response = await axios.post(`${API}/carriers/multi-quote`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  getCarrierQuotes: async () => {
    const response = await axios.get(`${API}/carriers/quotes`, { headers: getAuthHeaders() });
    return response.data;
  },

  bindCarrierPolicy: async (carrierId, data) => {
    const response = await axios.post(`${API}/carriers/${carrierId}/bind`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  getCarrierAdminConfig: async () => {
    const response = await axios.get(`${API}/carriers/admin/config`, { headers: getAuthHeaders() });
    return response.data;
  },

  getCarrierAdminStats: async () => {
    const response = await axios.get(`${API}/carriers/admin/stats`, { headers: getAuthHeaders() });
    return response.data;
  }
};
